import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { getLocalDateString, timeStringToMinutes } from './lib/time';
import { TIMELINE_ZOOM_LEVELS } from './lib/zoom';
import useTimelineData from './hooks/useTimelineData';
import useTimelineHeaderBridge from './hooks/useTimelineHeaderBridge';
import useTimelineSidebar from './hooks/useTimelineSidebar';
import useTimelineTicker from './hooks/useTimelineTicker';
import useTimelineViewport from './hooks/useTimelineViewport';
import {
  shiftTimelineDay,
  shiftTimelineWorkflow,
  shiftTimelineWorkflows,
} from './lib/api';

import TimelineHeader from './components/TimelineHeader';
import TimelineContextMenu from './components/TimelineContextMenu';
import TimelineHoverMarkerLines from './components/TimelineHoverMarkerLines';
import TimelineEmptyOnboarding from './components/TimelineEmptyOnboarding';
import TimelineSidebar from './components/TimelineSidebar';
import TimelineShiftModal from './components/TimelineShiftModal.jsx';
import TimelineTaskInfoModal from './components/TimelineTaskInfoModal.jsx';
import WorkflowRow from './components/WorkflowRow';

const START_HOUR = 0;
const END_HOUR = 24;
const BASE_SCALE = 4;
const SHIFT_DRAG_ACTIVATION_PX = 4;

function timeToPixels(minutes, zoom) {
  return (minutes - START_HOUR * 60) * BASE_SCALE * zoom;
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function getShiftDragSnapMinutes(zoom) {
  const currentZoom = Number(zoom) || 1;
  if (currentZoom <= 0.25) {
    return 30;
  }
  if (currentZoom <= 0.5) {
    return 15;
  }
  if (currentZoom <= 1) {
    return 5;
  }
  if (currentZoom <= 2.5) {
    return 2;
  }
  return 1;
}

function snapShiftDeltaMinutes(rawDeltaMinutes, zoom) {
  if (!Number.isFinite(rawDeltaMinutes)) {
    return 0;
  }

  const snapMinutes = getShiftDragSnapMinutes(zoom);
  return Math.round(rawDeltaMinutes / snapMinutes) * snapMinutes;
}

function parseTimelineClockValue(value) {
  const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isInteger(hours)
    || !Number.isInteger(minutes)
    || hours < 0
    || hours > 24
    || minutes < 0
    || minutes > 59
    || (hours === 24 && minutes !== 0)
  ) {
    return null;
  }

  return (hours * 60) + minutes;
}

function formatTimelineClockValue(totalMinutes) {
  const normalized = clampNumber(Math.round(Number(totalMinutes) || 0), 0, END_HOUR * 60);
  if (normalized >= END_HOUR * 60) {
    return '24:00';
  }

  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function shiftTimelineClockValue(value, deltaMinutes) {
  const minutes = parseTimelineClockValue(value);
  if (minutes === null) {
    return value;
  }

  return formatTimelineClockValue(minutes + deltaMinutes);
}

function shiftAnchorLabel(value, deltaMinutes) {
  const match = String(value ?? '').trim().match(/^(Start|Ende)\s+(\d{1,2}:\d{2})$/);
  if (!match) {
    return value;
  }

  return `${match[1]} ${shiftTimelineClockValue(match[2], deltaMinutes)}`;
}

function formatMinutesAsTimeValue(totalMinutes) {
  const normalized = Math.max(0, Math.min((END_HOUR * 60) - 1, Number(totalMinutes) || 0));
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function formatSignedDeltaMinutes(deltaMinutes) {
  const delta = Number(deltaMinutes) || 0;
  if (delta > 0) {
    return `+${delta} min`;
  }
  return `${delta} min`;
}

function dispatchTimelineEntityAction(detail) {
  document.dispatchEvent(new CustomEvent('ui:entity-action', {
    detail,
  }));
}

function guardBillingMutation() {
  return window.ToxFlowBillingAccess?.guardMutation?.() === true;
}

function dispatchToast({
  variant = 'secondary',
  title = 'Timeline',
  message = '',
  delay = 5000,
}) {
  document.dispatchEvent(new CustomEvent('ui:toast', {
    detail: {
      variant,
      title,
      message,
      delay,
    },
  }));
}

function buildTimelineRowOrderPayload(rows) {
  return rows
    .map((row) => {
      const isRegular = row?.rule_id !== null && row?.rule_id !== undefined;
      const rowId = isRegular ? row?.rule_id : row?.day_workflow_id;
      if (rowId === null || rowId === undefined || rowId === '') {
        return null;
      }

      return {
        entity: isRegular ? 'regular-workflows' : 'day-workflows',
        id: Number(rowId),
      };
    })
    .filter(Boolean);
}

function parseSourceTaskId(rawTaskId) {
  const raw = String(rawTaskId ?? '').trim();
  if (!raw) {
    return null;
  }

  const match = raw.match(/^(\d+):/);
  if (match) {
    return Number(match[1]);
  }

  return /^\d+$/.test(raw) ? Number(raw) : null;
}

function normalizeTimelineRowEntity(entity) {
  const value = String(entity ?? '').trim().toLowerCase().replace(/_/g, '-');
  if (value === 'regular-workflows' || value === 'day-workflows') {
    return value;
  }
  return '';
}

function buildTimelineRowHighlightKey(entity, id) {
  const normalizedEntity = normalizeTimelineRowEntity(entity);
  if (!normalizedEntity || id === null || id === undefined || id === '') {
    return '';
  }
  return `${normalizedEntity}:${String(id)}`;
}

function buildTimelineRowHighlightKeyFromWorkflow(row) {
  if (!row) {
    return '';
  }

  const isRegular = row?.rule_id !== null && row?.rule_id !== undefined;
  return buildTimelineRowHighlightKey(
    isRegular ? 'regular-workflows' : 'day-workflows',
    isRegular ? row?.rule_id : row?.day_workflow_id,
  );
}

function buildWorkflowShiftHoverState(dragState) {
  if (!dragState?.isActive) {
    return null;
  }

  const shiftItems = Array.isArray(dragState.items) && dragState.items.length
    ? dragState.items
    : [dragState];
  const shiftedStart = Math.min(
    ...shiftItems.map((item) => item.occurrenceStartMinutes + dragState.deltaMinutes)
  );
  const shiftedEnd = Math.max(
    ...shiftItems.map((item) => item.occurrenceEndMinutes + dragState.deltaMinutes)
  );
  const shiftedStartText = formatMinutesAsTimeValue(shiftedStart);
  const shiftedEndText = formatMinutesAsTimeValue(shiftedEnd);
  const deltaText = formatSignedDeltaMinutes(dragState.deltaMinutes);
  const isSelectionShift = shiftItems.length > 1 || dragState.mode === 'selection';

  return {
    kind: 'workflow-shift',
    taskId: `workflow-shift:${isSelectionShift ? 'selection' : dragState.rowKey}`,
    workflowId: isSelectionShift ? 'selection' : dragState.workflowRenderId,
    occurrenceId: isSelectionShift ? 'selection' : dragState.occurrenceId,
    color: isSelectionShift ? '#a1a9b4' : (dragState.color || '#ffffff'),
    combinedLabel: isSelectionShift
      ? `${shiftItems.length} Workflows ${deltaText}`
      : `${dragState.workflowName} ${deltaText}`,
    forceWorkflowHoverLabel: true,
    markers: [
      {
        key: 'shift-start',
        type: 'start',
        minutes: shiftedStart,
        label: `Neu Start ${shiftedStartText}`,
        timeText: shiftedStartText,
      },
      {
        key: 'shift-end',
        type: 'end',
        minutes: shiftedEnd,
        label: `Neu Ende ${shiftedEndText}`,
        timeText: shiftedEndText,
      },
    ],
  };
}

function optimisticallyShiftTimelineWorkflowRows(rows, shiftState) {
  if (!Array.isArray(rows) || !shiftState?.deltaMinutes) {
    return rows;
  }

  const rowKeys = new Set(
    (Array.isArray(shiftState.items) ? shiftState.items : [])
      .map((item) => item?.rowKey)
      .filter(Boolean)
  );
  if (shiftState.rowKey) {
    rowKeys.add(shiftState.rowKey);
  }
  if (rowKeys.size < 1) {
    return rows;
  }

  return rows.map((row) => {
    if (!rowKeys.has(buildTimelineRowHighlightKeyFromWorkflow(row))) {
      return row;
    }

    return {
      ...row,
      start_time: shiftTimelineClockValue(row.start_time, shiftState.deltaMinutes),
      computed_start_time: shiftTimelineClockValue(row.computed_start_time, shiftState.deltaMinutes),
      computed_end_time: shiftTimelineClockValue(row.computed_end_time, shiftState.deltaMinutes),
      anchor_time: shiftTimelineClockValue(row.anchor_time, shiftState.deltaMinutes),
      occurrences: (row.occurrences ?? []).map((occurrence) => ({
        ...occurrence,
        start: shiftTimelineClockValue(occurrence.start, shiftState.deltaMinutes),
        end: shiftTimelineClockValue(occurrence.end, shiftState.deltaMinutes),
        actual_start: shiftTimelineClockValue(occurrence.actual_start, shiftState.deltaMinutes),
        actual_end: shiftTimelineClockValue(occurrence.actual_end, shiftState.deltaMinutes),
        anchor_time: shiftTimelineClockValue(occurrence.anchor_time, shiftState.deltaMinutes),
        anchor_label: shiftAnchorLabel(occurrence.anchor_label, shiftState.deltaMinutes),
      })),
      tasks: (row.tasks ?? []).map((task) => ({
        ...task,
        start: shiftTimelineClockValue(task.start, shiftState.deltaMinutes),
        end: shiftTimelineClockValue(task.end, shiftState.deltaMinutes),
        actual_start: shiftTimelineClockValue(task.actual_start, shiftState.deltaMinutes),
        actual_end: shiftTimelineClockValue(task.actual_end, shiftState.deltaMinutes),
      })),
    };
  });
}

function getWorkflowTimelineEntity(row) {
  return row?.rule_id !== null && row?.rule_id !== undefined
    ? 'regular-workflows'
    : 'day-workflows';
}

function getWorkflowTimelineRowId(row) {
  return row?.rule_id !== null && row?.rule_id !== undefined
    ? row?.rule_id
    : row?.day_workflow_id;
}

function buildWorkflowShiftItemFromRow(row) {
  if (!row) {
    return null;
  }

  const workflowEntity = getWorkflowTimelineEntity(row);
  const workflowRowId = Number(getWorkflowTimelineRowId(row));
  const rowKey = buildTimelineRowHighlightKey(workflowEntity, workflowRowId);
  const occurrence = (row.occurrences ?? [])[0];
  const occurrenceStartMinutes = timeStringToMinutes(occurrence?.start);
  const occurrenceEndMinutes = timeStringToMinutes(occurrence?.end);

  if (
    !workflowEntity
    || !Number.isInteger(workflowRowId)
    || !rowKey
    || !occurrence
    || !Number.isFinite(occurrenceStartMinutes)
    || !Number.isFinite(occurrenceEndMinutes)
    || occurrenceEndMinutes <= occurrenceStartMinutes
  ) {
    return null;
  }

  return {
    rowKey,
    workflowRenderId: row.id,
    workflowId: row.workflow_id ?? null,
    workflowEntity,
    workflowRowId,
    workflowType: workflowEntity === 'regular-workflows' ? 'regular' : 'day',
    workflowName: row.name ?? '',
    timeMode: row.time_mode ?? 'start',
    anchorTime: row.anchor_time ?? row.start_time ?? null,
    occurrenceId: occurrence.occurrence_id,
    occurrenceStartMinutes,
    occurrenceEndMinutes,
    color: row.color || '#ffffff',
  };
}

function getWorkflowShiftBounds(items, totalMinutes) {
  const validItems = (items ?? []).filter((item) => (
    Number.isFinite(item?.occurrenceStartMinutes)
    && Number.isFinite(item?.occurrenceEndMinutes)
  ));

  if (validItems.length < 1) {
    return {
      minDeltaMinutes: 0,
      maxDeltaMinutes: 0,
    };
  }

  return {
    minDeltaMinutes: Math.max(
      ...validItems.map((item) => -item.occurrenceStartMinutes)
    ),
    maxDeltaMinutes: Math.min(
      ...validItems.map((item) => totalMinutes - item.occurrenceEndMinutes)
    ),
  };
}

function buildWorkflowShiftPayloadItems(items) {
  const seen = new Set();
  return (items ?? [])
    .map((item) => {
      const entity = normalizeTimelineRowEntity(item?.workflowEntity);
      const id = Number(item?.workflowRowId);
      const key = `${entity}:${id}`;
      if (!entity || !Number.isInteger(id) || seen.has(key)) {
        return null;
      }
      seen.add(key);
      return { entity, id };
    })
    .filter(Boolean);
}

function findTimelineWorkflowRow(rows, entity, rowId) {
  const normalizedEntity = normalizeTimelineRowEntity(entity);
  const normalizedRowId = Number(rowId);
  if (!normalizedEntity || !Number.isInteger(normalizedRowId)) {
    return null;
  }

  return (Array.isArray(rows) ? rows : []).find((row) => (
    getWorkflowTimelineEntity(row) === normalizedEntity
    && Number(getWorkflowTimelineRowId(row)) === normalizedRowId
  )) ?? null;
}

function buildShiftedWorkflowCopyEntityAction({
  rows,
  request,
  deltaMinutes,
  copyTarget,
}) {
  if (request?.mode !== 'workflow') {
    return null;
  }

  const context = request.context ?? {};
  const workflowEntity = normalizeTimelineRowEntity(context.workflowEntity);
  const workflowRowId = Number(context.workflowRowId);
  if (!workflowEntity || !Number.isInteger(workflowRowId)) {
    return null;
  }

  const sourceRow = findTimelineWorkflowRow(rows, workflowEntity, workflowRowId);
  const workflowId = Number(sourceRow?.workflow_id ?? context.workflowId);
  const sourceTime = String(
    sourceRow?.anchor_time
      ?? sourceRow?.start_time
      ?? context.anchorTime
      ?? context.timeValue
      ?? '09:00'
  );
  const timeMode = String(sourceRow?.time_mode ?? context.timeMode ?? 'start') === 'end'
    ? 'end'
    : 'start';
  const timeValue = shiftTimelineClockValue(sourceTime, deltaMinutes);

  if (workflowEntity === 'regular-workflows' && copyTarget === 'routine') {
    return {
      source: 'timeline-shift-copy',
      entity: 'regular-workflows',
      action: 'create',
      prefill: {
        copy_of_id: String(workflowRowId),
        time_mode: timeMode,
        time_value: timeValue,
      },
    };
  }

  if (!Number.isInteger(workflowId)) {
    return null;
  }

  return {
    source: 'timeline-shift-copy',
    entity: 'day-workflows',
    action: 'create',
    prefill: {
      workflow_id: workflowId,
      date: String(request.date ?? ''),
      time_mode: timeMode,
      time_value: timeValue,
    },
  };
}

function rectanglesIntersect(left, right) {
  return (
    left.left < right.right
    && left.right > right.left
    && left.top < right.bottom
    && left.bottom > right.top
  );
}

function shouldIgnoreTimelineMarqueeTarget(target) {
  return Boolean(target?.closest?.([
    '.task-block',
    '.workflow-occurrence-track',
    '.workflow-occurrence-track-label',
    '.workflow-row-drag-handle',
    '.timeline-context-menu',
    '.timeline-header',
    '.timeline-shift-modal',
    '.modal',
    'button',
    'input',
    'select',
    'textarea',
    'a',
  ].join(',')));
}

function buildMarqueeSelectionStyle(selection) {
  if (!selection?.isActive) {
    return null;
  }

  const left = Math.min(selection.startX, selection.currentX);
  const top = Math.min(selection.startY, selection.currentY);
  const width = Math.abs(selection.currentX - selection.startX);
  const height = Math.abs(selection.currentY - selection.startY);

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
  };
}

function buildTaskInfoState(detail, selectedDate) {
  return {
    taskId: detail?.taskId ?? null,
    taskName: String(detail?.taskName ?? '').trim(),
    workflowId: detail?.workflowId ?? null,
    workflowName: String(detail?.workflowName ?? '').trim(),
    workflowType: String(detail?.workflowType ?? '').trim(),
    workflowEntity: String(detail?.workflowEntity ?? '').trim(),
    workflowRowId: detail?.workflowRowId ?? null,
    durationMinutes: Number(detail?.durationMinutes ?? 0) || 0,
    relOffsetMinutes: Number(detail?.relOffsetMinutes ?? 0) || 0,
    offsetReferenceKind: String(detail?.offsetReferenceKind ?? 'previous_task').trim(),
    actualStart: String(detail?.actualStart ?? '').trim(),
    actualEnd: String(detail?.actualEnd ?? '').trim(),
    startsPreviousDay: Boolean(detail?.startsPreviousDay),
    endsNextDay: Boolean(detail?.endsNextDay),
    occurrenceDate: String(detail?.occurrenceDate ?? selectedDate ?? '').trim(),
    description: String(detail?.description ?? ''),
    otherActiveTaskCount: Math.max(0, Number(detail?.otherActiveTaskCount ?? 0) || 0),
  };
}

function buildTaskInfoStateFromTimelineTask({
  task,
  workflow,
  selectedDate,
  otherActiveTaskCount = 0,
}) {
  const workflowEntity = getWorkflowTimelineEntity(workflow);
  const workflowType = workflowEntity === 'regular-workflows' ? 'regular' : 'day';

  return buildTaskInfoState({
    taskId: task?.task_id,
    taskName: task?.name,
    workflowId: workflow?.workflow_id ?? workflow?.id,
    workflowName: workflow?.name,
    workflowType,
    workflowEntity,
    workflowRowId: getWorkflowTimelineRowId(workflow),
    durationMinutes: task?.duration_minutes,
    relOffsetMinutes: task?.rel_offset_minutes,
    offsetReferenceKind: task?.offset_reference_kind,
    actualStart: task?.actual_start ?? task?.start,
    actualEnd: task?.actual_end ?? task?.end,
    startsPreviousDay: Boolean(task?.starts_previous_day),
    endsNextDay: Boolean(task?.ends_next_day),
    occurrenceDate: selectedDate,
    description: task?.description ?? '',
    otherActiveTaskCount,
  }, selectedDate);
}

const TASK_INFO_COMPARE_KEYS = [
  'taskId',
  'taskName',
  'workflowId',
  'workflowName',
  'workflowType',
  'workflowEntity',
  'workflowRowId',
  'durationMinutes',
  'relOffsetMinutes',
  'offsetReferenceKind',
  'actualStart',
  'actualEnd',
  'startsPreviousDay',
  'endsNextDay',
  'occurrenceDate',
  'description',
  'otherActiveTaskCount',
];

function areTaskInfoStatesEqual(left, right) {
  return TASK_INFO_COMPARE_KEYS.every((key) => String(left?.[key] ?? '') === String(right?.[key] ?? ''));
}

function getTaskRecordId(record) {
  const rawId = record?.id ?? record?.task_id;
  const id = Number(rawId);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function hasOwnField(record, fieldName) {
  return Boolean(record && Object.prototype.hasOwnProperty.call(record, fieldName));
}

function mergeTaskInfoWithTaskRecord(currentTaskInfo, taskRecord) {
  if (!currentTaskInfo || !taskRecord) {
    return currentTaskInfo;
  }

  const durationRaw = taskRecord.duration_min;
  const durationMinutes = (
    durationRaw !== null
    && durationRaw !== undefined
    && String(durationRaw).trim() !== ''
  )
    ? Number(durationRaw)
    : null;

  return {
    ...currentTaskInfo,
    taskName: hasOwnField(taskRecord, 'name')
      ? String(taskRecord.name ?? '').trim()
      : currentTaskInfo.taskName,
    description: hasOwnField(taskRecord, 'description')
      ? String(taskRecord.description ?? '')
      : currentTaskInfo.description,
    durationMinutes: Number.isFinite(durationMinutes)
      ? durationMinutes
      : currentTaskInfo.durationMinutes,
  };
}

function findRefreshedTaskInfo(currentTaskInfo, workflows, selectedDate) {
  if (!currentTaskInfo) {
    return null;
  }

  const sourceTaskId = parseSourceTaskId(currentTaskInfo.taskId);
  if (!Number.isInteger(sourceTaskId) || sourceTaskId < 1) {
    return null;
  }

  const targetEntity = normalizeTimelineRowEntity(currentTaskInfo.workflowEntity);
  const targetRowId = String(currentTaskInfo.workflowRowId ?? '').trim();
  const targetWorkflowId = String(currentTaskInfo.workflowId ?? '').trim();
  const targetActualStart = String(currentTaskInfo.actualStart ?? '').trim();
  let fallbackMatch = null;

  for (const workflow of workflows ?? []) {
    const workflowEntity = getWorkflowTimelineEntity(workflow);
    const workflowRowId = String(getWorkflowTimelineRowId(workflow) ?? '').trim();
    const workflowId = String(workflow?.workflow_id ?? workflow?.id ?? '').trim();

    if (targetEntity && workflowEntity !== targetEntity) {
      continue;
    }

    if (targetRowId && workflowRowId !== targetRowId) {
      continue;
    }

    if (!targetRowId && targetWorkflowId && workflowId !== targetWorkflowId) {
      continue;
    }

    for (const task of workflow?.tasks ?? []) {
      if (parseSourceTaskId(task?.task_id) !== sourceTaskId) {
        continue;
      }

      const refreshed = buildTaskInfoStateFromTimelineTask({
        task,
        workflow,
        selectedDate,
        otherActiveTaskCount: currentTaskInfo.otherActiveTaskCount,
      });

      if (!fallbackMatch) {
        fallbackMatch = refreshed;
      }

      const taskActualStart = String(task?.actual_start ?? task?.start ?? '').trim();
      if (!targetActualStart || taskActualStart === targetActualStart) {
        return refreshed;
      }
    }
  }

  return fallbackMatch;
}

export default function TimelineApp() {
  const [zoom, setZoom] = useState(1.0);
  const [liveFollowActive, setLiveFollowActive] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [hoveredTimelineMarkers, setHoveredTimelineMarkers] = useState(null);
  const [timelineContextMenu, setTimelineContextMenu] = useState(null);
  const [taskInfoModal, setTaskInfoModal] = useState(null);
  const [shiftRequest, setShiftRequest] = useState(null);
  const [shiftBusy, setShiftBusy] = useState(false);
  const [currentMinute, setCurrentMinute] = useState(() => {
    const now = new Date();
    return (now.getHours() * 60) + now.getMinutes();
  });
  const [workflowShiftDrag, setWorkflowShiftDrag] = useState(null);
  const [selectedWorkflowKeys, setSelectedWorkflowKeys] = useState([]);
  const [marqueeSelection, setMarqueeSelection] = useState(null);
  const [recentlyInsertedRowKeys, setRecentlyInsertedRowKeys] = useState([]);
  const [dateChangeHighlightActive, setDateChangeHighlightActive] = useState(false);
  const [starterTemplateImporting, setStarterTemplateImporting] = useState(false);
  const taskInfoModalRef = useRef(null);
  const timelineContentRef = useRef(null);
  const workflowShiftDragRef = useRef(null);
  const marqueeSelectionRef = useRef(null);
  const pendingInsertedRowKeysRef = useRef(new Set());
  const insertedRowTimeoutsRef = useRef(new Map());
  const pendingDateChangeHighlightDateRef = useRef(null);
  const dateChangeHighlightTimeoutRef = useRef(null);
  const previousSelectedDateRef = useRef(selectedDate);

  const zoomMin = TIMELINE_ZOOM_LEVELS[0];
  const zoomMax = TIMELINE_ZOOM_LEVELS[TIMELINE_ZOOM_LEVELS.length - 1];

  const totalMinutes = (END_HOUR - START_HOUR) * 60;

  const isToday = useMemo(
    () => selectedDate === getLocalDateString(),
    [selectedDate]
  );

  const timelineWidthPx = useMemo(
    () => totalMinutes * BASE_SCALE * zoom,
    [totalMinutes, zoom]
  );

  const headerStep = useMemo(
    () => (zoom > 1.6 ? 15 : zoom > 1.2 ? 30 : 60),
    [zoom]
  );

  const hours = useMemo(() => {
    const arr = [];
    for (let m = 0; m <= totalMinutes; m += headerStep) {
      arr.push(m);
    }
    return arr;
  }, [headerStep, totalMinutes]);

  const {
    workflows,
    setWorkflows,
    loading: timelineLoading,
    loadedDate,
    reload,
    persistRowOrder,
  } = useTimelineData(selectedDate);
  const selectedWorkflowKeySet = useMemo(
    () => new Set(selectedWorkflowKeys),
    [selectedWorkflowKeys]
  );
  const marqueePreviewWorkflowKeySet = useMemo(
    () => new Set(
      marqueeSelection?.isActive
        ? (marqueeSelection.previewWorkflowKeys || [])
        : []
    ),
    [marqueeSelection]
  );
  const selectedWorkflowShiftItems = useMemo(() => {
    if (!selectedWorkflowKeySet.size) {
      return [];
    }

    return (workflows ?? [])
      .map((row) => buildWorkflowShiftItemFromRow(row))
      .filter((item) => item && selectedWorkflowKeySet.has(item.rowKey));
  }, [selectedWorkflowKeySet, workflows]);
  const selectedWorkflowCount = selectedWorkflowShiftItems.length;
  const selectedWorkflowContainsRegular = useMemo(
    () => selectedWorkflowShiftItems.some((item) => item.workflowType === 'regular'),
    [selectedWorkflowShiftItems]
  );
  const sidebar = useTimelineSidebar(selectedDate);
  const isTimelineEmpty = !timelineLoading && !starterTemplateImporting && (workflows ?? []).length < 1;
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 140,
        tolerance: 8,
      },
    }),
  );

  const {
    scrollRef,
    scrollToNow,
    updateCurrentTimeLine,
    zoomWithAnchor,
  } = useTimelineViewport({
    zoom,
    setZoom,
    zoomMin,
    zoomMax,
    timeToPixels,
    liveFollowActive,
    setLiveFollowActive,
    isToday,
  });

  useTimelineHeaderBridge({
    zoom,
    liveFollowActive,
    selectedDate,
    isToday,
    zoomWithAnchor,
    scrollToNow,
    setSelectedDate,
    setLiveFollowActive,
  });

  useTimelineTicker({
    isToday,
    liveFollowActive,
    scrollToNow,
    updateCurrentTimeLine,
    reload,
    setSelectedDate,
    onMinuteTick: setCurrentMinute,
  });

  useEffect(() => {
    const now = new Date();
    setCurrentMinute((now.getHours() * 60) + now.getMinutes());
  }, [selectedDate]);

  const closeTaskInfoModal = useCallback(() => setTaskInfoModal(null), []);

  const handleStarterTemplateImported = useCallback(async (result) => {
    const rows = Array.isArray(result?.timeline_rows) ? result.timeline_rows : [];
    rows.forEach((row) => {
      const key = buildTimelineRowHighlightKey(row?.entity, row?.id);
      if (key) {
        pendingInsertedRowKeysRef.current.add(key);
      }
    });
    await reload(selectedDate);
  }, [reload, selectedDate]);

  useEffect(() => {
    taskInfoModalRef.current = taskInfoModal;
  }, [taskInfoModal]);

  useEffect(() => {
    updateCurrentTimeLine();

    if (isToday) {
      scrollToNow(true);
    }
    // intentionally only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setHoveredTimelineMarkers(null);
    setTimelineContextMenu(null);
  }, [selectedDate, workflows]);

  useEffect(() => {
    setSelectedWorkflowKeys([]);
    setMarqueeSelection(null);
  }, [selectedDate]);

  useEffect(() => {
    if (previousSelectedDateRef.current === selectedDate) {
      return;
    }

    previousSelectedDateRef.current = selectedDate;
    pendingDateChangeHighlightDateRef.current = selectedDate;
    setDateChangeHighlightActive(false);

    if (dateChangeHighlightTimeoutRef.current) {
      clearTimeout(dateChangeHighlightTimeoutRef.current);
      dateChangeHighlightTimeoutRef.current = null;
    }
  }, [selectedDate]);

  useEffect(() => {
    if (
      timelineLoading
      || loadedDate !== selectedDate
      || pendingDateChangeHighlightDateRef.current !== selectedDate
    ) {
      return;
    }

    pendingDateChangeHighlightDateRef.current = null;
    setDateChangeHighlightActive(true);
    dateChangeHighlightTimeoutRef.current = window.setTimeout(() => {
      setDateChangeHighlightActive(false);
      dateChangeHighlightTimeoutRef.current = null;
    }, 1800);
  }, [loadedDate, selectedDate, timelineLoading]);

  useEffect(() => {
    if (!selectedWorkflowKeys.length) {
      return;
    }

    const availableKeys = new Set(
      (workflows ?? [])
        .map((row) => buildTimelineRowHighlightKeyFromWorkflow(row))
        .filter(Boolean)
    );
    const nextKeys = selectedWorkflowKeys.filter((key) => availableKeys.has(key));
    if (nextKeys.length !== selectedWorkflowKeys.length) {
      setSelectedWorkflowKeys(nextKeys);
    }
  }, [selectedWorkflowKeys, workflows]);

  useEffect(() => () => {
    document.body.classList.remove('is-timeline-workflow-shift-dragging');
    document.body.classList.remove('is-timeline-marquee-selecting');
  }, []);

  useEffect(() => () => {
    insertedRowTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    insertedRowTimeoutsRef.current.clear();

    if (dateChangeHighlightTimeoutRef.current) {
      clearTimeout(dateChangeHighlightTimeoutRef.current);
      dateChangeHighlightTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    function onTimelineRowCreated(event) {
      const entity = normalizeTimelineRowEntity(event?.detail?.entity);
      const id = String(event?.detail?.id ?? '').trim();
      const key = buildTimelineRowHighlightKey(entity, id);
      if (!key) {
        return;
      }

      pendingInsertedRowKeysRef.current.add(key);
    }

    document.addEventListener('timeline:row-created', onTimelineRowCreated);
    return () => document.removeEventListener('timeline:row-created', onTimelineRowCreated);
  }, []);

  useEffect(() => {
    if (!pendingInsertedRowKeysRef.current.size) {
      return;
    }

    const availableKeys = new Set(
      (Array.isArray(workflows) ? workflows : [])
        .map((row) => buildTimelineRowHighlightKeyFromWorkflow(row))
        .filter(Boolean)
    );

    const matchedKeys = Array.from(pendingInsertedRowKeysRef.current)
      .filter((key) => availableKeys.has(key));

    if (matchedKeys.length < 1) {
      return;
    }

    matchedKeys.forEach((key) => {
      pendingInsertedRowKeysRef.current.delete(key);

      const existingTimeout = insertedRowTimeoutsRef.current.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const timeoutId = window.setTimeout(() => {
        setRecentlyInsertedRowKeys((current) => current.filter((entry) => entry !== key));
        insertedRowTimeoutsRef.current.delete(key);
      }, 1800);

      insertedRowTimeoutsRef.current.set(key, timeoutId);
    });

    setRecentlyInsertedRowKeys((current) => Array.from(new Set([...current, ...matchedKeys])));
  }, [workflows]);

  useEffect(() => {
    if (!timelineContextMenu?.open) {
      return undefined;
    }

    function closeMenu() {
      setTimelineContextMenu(null);
    }

    function onWindowClick() {
      closeMenu();
    }

    function onEscape(event) {
      if (event.key === 'Escape') {
        closeMenu();
      }
    }

    function onScroll() {
      closeMenu();
    }

    window.addEventListener('click', onWindowClick);
    window.addEventListener('keydown', onEscape);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('click', onWindowClick);
      window.removeEventListener('keydown', onEscape);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [timelineContextMenu?.open]);

  useEffect(() => {
    function onOpenTaskInfo(event) {
      const detail = event?.detail;
      if (!detail?.taskName) {
        return;
      }

      setTaskInfoModal(buildTaskInfoState(detail, selectedDate));
    }

    document.addEventListener('timeline:open-task-info', onOpenTaskInfo);
    return () => {
      document.removeEventListener('timeline:open-task-info', onOpenTaskInfo);
    };
  }, [selectedDate]);

  useEffect(() => {
    function onCrudUpdated(event) {
      const entity = String(event?.detail?.entity ?? '').trim();
      if (entity !== 'tasks') {
        return;
      }

      const taskRecord = event?.detail?.record;
      const updatedTaskId = getTaskRecordId(taskRecord);
      const currentTaskInfo = taskInfoModalRef.current;
      if (
        !updatedTaskId
        || parseSourceTaskId(currentTaskInfo?.taskId) !== updatedTaskId
      ) {
        return;
      }

      setTaskInfoModal((current) => {
        if (parseSourceTaskId(current?.taskId) !== updatedTaskId) {
          return current;
        }

        return mergeTaskInfoWithTaskRecord(current, taskRecord);
      });

      const refreshDate = selectedDate;
      void reload(refreshDate)
        .then((nextWorkflows) => {
          setTaskInfoModal((current) => {
            const refreshed = findRefreshedTaskInfo(current, nextWorkflows, refreshDate);
            if (!refreshed || areTaskInfoStatesEqual(current, refreshed)) {
              return current;
            }

            return refreshed;
          });
        })
        .catch((error) => {
          dispatchToast({
            variant: 'warning',
            title: 'Task-Information',
            message: `Task wurde gespeichert, aber die Timeline konnte nicht neu geladen werden: ${error?.message ?? String(error)}`,
            delay: 7000,
          });
        });
    }

    document.addEventListener('crud:updated', onCrudUpdated);
    return () => document.removeEventListener('crud:updated', onCrudUpdated);
  }, [reload, selectedDate]);

  useEffect(() => {
    setTaskInfoModal((current) => {
      const refreshed = findRefreshedTaskInfo(current, workflows, selectedDate);
      if (!refreshed || areTaskInfoStatesEqual(current, refreshed)) {
        return current;
      }

      return refreshed;
    });
  }, [selectedDate, workflows]);

  const buildContextTimeValue = useCallback((clientX) => {
    const scroller = scrollRef.current;
    if (!scroller) {
      return '09:00';
    }

    const rect = scroller.getBoundingClientRect();
    const contentX = Math.max(0, (clientX - rect.left) + scroller.scrollLeft);
    const minutes = Math.round(contentX / (BASE_SCALE * zoom));
    return formatMinutesAsTimeValue(minutes);
  }, [scrollRef, zoom]);

  const toggleWorkflowSelection = useCallback((detail) => {
    const rowKey = String(
      detail?.workflowRowKey
      || buildTimelineRowHighlightKey(detail?.workflowEntity, detail?.workflowRowId)
      || ''
    ).trim();

    if (!rowKey) {
      return;
    }

    setTimelineContextMenu(null);
    setSelectedWorkflowKeys((current) => (
      current.includes(rowKey)
        ? current.filter((key) => key !== rowKey)
        : [...current, rowKey]
    ));
  }, []);

  const clearWorkflowSelection = useCallback(() => {
    setSelectedWorkflowKeys([]);
    setMarqueeSelection(null);
  }, []);

  useEffect(() => {
    if (selectedWorkflowCount < 1 || shiftRequest || timelineContextMenu?.open) {
      return undefined;
    }

    function onEscape(event) {
      if (
        event.key !== 'Escape'
        || event.defaultPrevented
        || event.target?.closest?.('.modal, .timeline-context-menu')
        || document.querySelector('.modal.show')
      ) {
        return;
      }

      event.preventDefault();
      clearWorkflowSelection();
    }

    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [clearWorkflowSelection, selectedWorkflowCount, shiftRequest, timelineContextMenu?.open]);

  const openSelectedWorkflowShift = useCallback(() => {
    if (selectedWorkflowShiftItems.length < 1) {
      dispatchToast({
        variant: 'warning',
        title: 'Timeline',
        message: 'Wähle zuerst mindestens einen Workflow auf der Timeline aus.',
      });
      return;
    }

    if (guardBillingMutation()) {
      return;
    }

    setShiftRequest({
      mode: 'selection',
      date: selectedDate,
      context: {
        items: selectedWorkflowShiftItems,
        selectionCount: selectedWorkflowShiftItems.length,
        containsRegular: selectedWorkflowContainsRegular,
      },
    });
  }, [selectedDate, selectedWorkflowContainsRegular, selectedWorkflowShiftItems]);

  const collectMarqueeWorkflowKeys = useCallback((selectionRect) => {
    const content = timelineContentRef.current;
    if (!content || !selectionRect) {
      return [];
    }

    const selectedKeys = new Set();
    content
      .querySelectorAll('[data-timeline-selectable-workflow="true"][data-workflow-row-key]')
      .forEach((node) => {
        const nodeRect = node.getBoundingClientRect();
        if (rectanglesIntersect(selectionRect, nodeRect)) {
          const key = String(node.dataset.workflowRowKey ?? '').trim();
          if (key) {
            selectedKeys.add(key);
          }
        }
      });

    return Array.from(selectedKeys);
  }, []);

  const startMarqueeSelection = useCallback((event) => {
    if (
      shiftBusy
      || event.button !== 0
      || event.pointerType !== 'mouse'
      || shouldIgnoreTimelineMarqueeTarget(event.target)
    ) {
      return;
    }

    const content = timelineContentRef.current;
    if (!content) {
      return;
    }

    const contentRect = content.getBoundingClientRect();
    const startX = event.clientX - contentRect.left;
    const startY = event.clientY - contentRect.top;
    const baseState = {
      startX,
      startY,
      currentX: startX,
      currentY: startY,
      startClientX: event.clientX,
      startClientY: event.clientY,
      currentClientX: event.clientX,
      currentClientY: event.clientY,
      isActive: false,
      previewWorkflowKeys: [],
    };

    event.preventDefault();
    setTimelineContextMenu(null);
    marqueeSelectionRef.current = baseState;
    setMarqueeSelection(baseState);
    document.body.classList.add('is-timeline-marquee-selecting');

    const updateMarquee = (pointerEvent) => {
      const currentX = pointerEvent.clientX - contentRect.left;
      const currentY = pointerEvent.clientY - contentRect.top;
      const isActive = (
        Math.abs(pointerEvent.clientX - baseState.startClientX) >= 4
        || Math.abs(pointerEvent.clientY - baseState.startClientY) >= 4
      );
      const selectionRect = isActive
        ? {
          left: Math.min(baseState.startClientX, pointerEvent.clientX),
          right: Math.max(baseState.startClientX, pointerEvent.clientX),
          top: Math.min(baseState.startClientY, pointerEvent.clientY),
          bottom: Math.max(baseState.startClientY, pointerEvent.clientY),
        }
        : null;
      const nextState = {
        ...baseState,
        currentX,
        currentY,
        currentClientX: pointerEvent.clientX,
        currentClientY: pointerEvent.clientY,
        isActive,
        previewWorkflowKeys: isActive ? collectMarqueeWorkflowKeys(selectionRect) : [],
      };

      marqueeSelectionRef.current = nextState;
      setMarqueeSelection(nextState);
    };

    const finishMarquee = (pointerEvent, canceled = false) => {
      window.removeEventListener('pointermove', updateMarquee);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
      document.body.classList.remove('is-timeline-marquee-selecting');

      if (!canceled) {
        updateMarquee(pointerEvent);
      }

      const finalState = marqueeSelectionRef.current;
      marqueeSelectionRef.current = null;
      setMarqueeSelection(null);

      if (canceled) {
        return;
      }

      if (!finalState?.isActive) {
        clearWorkflowSelection();
        return;
      }

      const selectionRect = {
        left: Math.min(finalState.startClientX, finalState.currentClientX),
        right: Math.max(finalState.startClientX, finalState.currentClientX),
        top: Math.min(finalState.startClientY, finalState.currentClientY),
        bottom: Math.max(finalState.startClientY, finalState.currentClientY),
      };

      setSelectedWorkflowKeys(collectMarqueeWorkflowKeys(selectionRect));
    };

    function handlePointerUp(pointerEvent) {
      finishMarquee(pointerEvent, false);
    }

    function handlePointerCancel(pointerEvent) {
      finishMarquee(pointerEvent, true);
    }

    window.addEventListener('pointermove', updateMarquee);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
  }, [clearWorkflowSelection, collectMarqueeWorkflowKeys, shiftBusy]);

  const openTimelineContextMenu = useCallback((event, context = {}) => {
    const timeValue = buildContextTimeValue(event.clientX);
    setTimelineContextMenu({
      open: true,
      x: event.clientX,
      y: event.clientY,
      context: {
        ...context,
        date: selectedDate,
        timeValue,
      },
    });
  }, [buildContextTimeValue, selectedDate]);

  const resolveTimelineContext = useCallback((event) => {
    const target = event.target;
    if (!target?.closest) {
      return { type: 'surface' };
    }

    const taskElement = target.closest('.task-block');
    if (taskElement) {
      return {
        type: 'task',
        taskId: parseSourceTaskId(taskElement.dataset.taskId),
        taskName: taskElement.dataset.taskName ?? '',
        workflowId: taskElement.dataset.workflowId ? Number(taskElement.dataset.workflowId) : null,
        workflowName: taskElement.dataset.workflowName ?? '',
        workflowType: taskElement.dataset.workflowType ?? 'day',
        workflowEntity: taskElement.dataset.workflowEntity ?? 'day-workflows',
        workflowRowId: taskElement.dataset.workflowRowId ? Number(taskElement.dataset.workflowRowId) : null,
      };
    }

    const workflowRowElement = target.closest('[data-workflow-row="true"]');
    if (workflowRowElement) {
      return {
        type: 'workflow',
        workflowId: workflowRowElement.dataset.workflowId ? Number(workflowRowElement.dataset.workflowId) : null,
        workflowName: workflowRowElement.dataset.workflowName ?? '',
        workflowType: workflowRowElement.dataset.workflowType ?? 'day',
        workflowEntity: workflowRowElement.dataset.workflowEntity ?? 'day-workflows',
        workflowRowId: workflowRowElement.dataset.workflowRowId ? Number(workflowRowElement.dataset.workflowRowId) : null,
      };
    }

    return { type: 'surface' };
  }, []);

  const createDayWorkflowFromContext = useCallback((context) => {
    dispatchTimelineEntityAction({
      source: 'timeline-context-menu',
      entity: 'day-workflows',
      action: 'create',
      prefill: {
        workflow_id: context?.workflowId ? Number(context.workflowId) : '',
        date: String(context?.date ?? selectedDate),
        time_mode: 'start',
        time_value: String(context?.timeValue ?? '09:00'),
      },
    });
  }, [selectedDate]);

  const createRegularWorkflowFromContext = useCallback((context) => {
    dispatchTimelineEntityAction({
      source: 'timeline-context-menu',
      entity: 'regular-workflows',
      action: 'create',
      prefill: {
        workflow_id: context?.workflowId ? Number(context.workflowId) : '',
        start_date: String(context?.date ?? selectedDate),
        time_mode: 'start',
        time_value: String(context?.timeValue ?? '09:00'),
      },
    });
  }, [selectedDate]);

  const editWorkflowFromContext = useCallback((context) => {
    if (!context?.workflowEntity || !context?.workflowRowId) {
      return;
    }

    dispatchTimelineEntityAction({
      source: 'timeline-context-menu',
      entity: context.workflowEntity,
      action: 'edit',
      rowId: String(context.workflowRowId),
      selectedIds: [String(context.workflowRowId)],
    });
  }, []);

  const deleteWorkflowFromContext = useCallback((context) => {
    if (!context?.workflowEntity || !context?.workflowRowId) {
      return;
    }

    dispatchTimelineEntityAction({
      source: 'timeline-context-menu',
      entity: context.workflowEntity,
      action: 'delete',
      selectedIds: [String(context.workflowRowId)],
    });
  }, []);

  const editTaskFromContext = useCallback((context) => {
    if (!context?.taskId) {
      return;
    }

    dispatchTimelineEntityAction({
      source: 'timeline-context-menu',
      entity: 'tasks',
      action: 'edit',
      rowId: String(context.taskId),
      selectedIds: [String(context.taskId)],
    });
  }, []);

  const shiftWorkflowFromContext = useCallback((context) => {
    if (!context?.workflowEntity || !context?.workflowRowId) {
      return;
    }

    if (guardBillingMutation()) {
      return;
    }

    const workflowEntity = normalizeTimelineRowEntity(context.workflowEntity);
    const workflowRowId = Number(context.workflowRowId);
    const sourceRow = findTimelineWorkflowRow(workflows, workflowEntity, workflowRowId);
    const sourceOccurrence = (sourceRow?.occurrences ?? [])[0] ?? null;
    const occurrenceStartMinutes = timeStringToMinutes(sourceOccurrence?.start);
    const occurrenceEndMinutes = timeStringToMinutes(sourceOccurrence?.end);

    setShiftRequest({
      mode: 'workflow',
      date: String(context?.date ?? selectedDate),
      context: {
        workflowEntity: workflowEntity || context.workflowEntity,
        workflowRowId,
        workflowType: context.workflowType ?? (workflowEntity === 'regular-workflows' ? 'regular' : 'day'),
        workflowName: context.workflowName ?? sourceRow?.name ?? '',
        workflowId: sourceRow?.workflow_id ?? context.workflowId ?? null,
        timeMode: sourceRow?.time_mode ?? context.timeMode ?? 'start',
        anchorTime: sourceRow?.anchor_time ?? sourceRow?.start_time ?? context.timeValue ?? null,
        occurrenceStartMinutes: Number.isFinite(occurrenceStartMinutes)
          ? occurrenceStartMinutes
          : null,
        occurrenceEndMinutes: Number.isFinite(occurrenceEndMinutes)
          ? occurrenceEndMinutes
          : null,
      },
    });
  }, [selectedDate, workflows]);

  const shiftDayFromContext = useCallback((context) => {
    if (guardBillingMutation()) {
      return;
    }

    setShiftRequest({
      mode: 'day',
      date: String(context?.date ?? selectedDate),
      context: {
        timeValue: context?.timeValue ?? null,
      },
    });
  }, [selectedDate]);

  const applyWorkflowShift = useCallback(async ({
    date,
    workflowEntity,
    workflowRowId,
    deltaMinutes,
    regularScope = 'day',
  }) => {
    setShiftBusy(true);
    try {
      await shiftTimelineWorkflow({
        date,
        entity: workflowEntity,
        id: workflowRowId,
        delta_minutes: deltaMinutes,
        regular_scope: regularScope,
      });

      dispatchToast({
        variant: 'success',
        title: 'Timeline',
        message: 'Workflow verschoben.',
      });

      await reload(date);
      return true;
    } catch (error) {
      dispatchToast({
        variant: 'danger',
        title: 'Timeline',
        message: `Verschieben fehlgeschlagen: ${error?.message ?? String(error)}`,
        delay: 7000,
      });
      return false;
    } finally {
      setShiftBusy(false);
    }
  }, [reload]);

  const startWorkflowShiftDrag = useCallback((event, detail) => {
    if (
      shiftBusy
      || event.button !== 0
      || event.pointerType === 'touch'
      || !detail?.occurrence
    ) {
      return;
    }

    const workflowEntity = normalizeTimelineRowEntity(detail.workflowEntity);
    const workflowRowId = Number(detail.workflowRowId);
    const occurrenceStartMinutes = timeStringToMinutes(detail.occurrence.start);
    const occurrenceEndMinutes = timeStringToMinutes(detail.occurrence.end);
    const rowKey = buildTimelineRowHighlightKey(workflowEntity, workflowRowId);

    if (
      !workflowEntity
      || !Number.isInteger(workflowRowId)
      || !rowKey
      || !Number.isFinite(occurrenceStartMinutes)
      || !Number.isFinite(occurrenceEndMinutes)
      || occurrenceEndMinutes <= occurrenceStartMinutes
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const singleShiftItem = {
      rowKey,
      workflowRenderId: detail.workflowRenderId,
      workflowId: detail.workflowId ? Number(detail.workflowId) : null,
      workflowEntity,
      workflowRowId,
      workflowType: detail.workflowType ?? 'day',
      workflowName: detail.workflowName ?? '',
      timeMode: detail.timeMode ?? 'start',
      anchorTime: detail.anchorTime ?? detail.occurrence?.anchor_time ?? null,
      occurrenceId: detail.occurrence.occurrence_id,
      occurrenceStartMinutes,
      occurrenceEndMinutes,
      color: detail.color || '#ffffff',
    };
    const shouldDragSelection = selectedWorkflowKeySet.has(rowKey) && selectedWorkflowShiftItems.length > 0;
    const shiftItems = shouldDragSelection ? selectedWorkflowShiftItems : [singleShiftItem];
    const { minDeltaMinutes, maxDeltaMinutes } = getWorkflowShiftBounds(shiftItems, totalMinutes);

    if (!shouldDragSelection && selectedWorkflowKeys.length) {
      setSelectedWorkflowKeys([]);
    }

    const baseState = {
      date: selectedDate,
      mode: shouldDragSelection ? 'selection' : 'workflow',
      rowKey,
      items: shiftItems,
      workflowRenderId: singleShiftItem.workflowRenderId,
      workflowId: singleShiftItem.workflowId,
      workflowEntity,
      workflowRowId,
      workflowType: singleShiftItem.workflowType,
      workflowName: singleShiftItem.workflowName,
      timeMode: singleShiftItem.timeMode,
      anchorTime: singleShiftItem.anchorTime,
      occurrenceId: singleShiftItem.occurrenceId,
      occurrenceStartMinutes,
      occurrenceEndMinutes,
      minDeltaMinutes,
      maxDeltaMinutes,
      color: detail.color || '#ffffff',
      startClientX: event.clientX,
      deltaMinutes: 0,
      isActive: false,
    };

    workflowShiftDragRef.current = baseState;
    setWorkflowShiftDrag(baseState);
    setTimelineContextMenu(null);
    document.body.classList.add('is-timeline-workflow-shift-dragging');

    const updateDrag = (pointerEvent) => {
      const current = workflowShiftDragRef.current ?? baseState;
      const pixelDelta = pointerEvent.clientX - baseState.startClientX;
      const rawDeltaMinutes = pixelDelta / (BASE_SCALE * zoom);
      const deltaMinutes = clampNumber(
        snapShiftDeltaMinutes(rawDeltaMinutes, zoom),
        baseState.minDeltaMinutes,
        baseState.maxDeltaMinutes,
      );
      const nextState = {
        ...baseState,
        deltaMinutes,
        isActive: current.isActive
          || Math.abs(pixelDelta) >= SHIFT_DRAG_ACTIVATION_PX
          || deltaMinutes !== 0,
      };

      workflowShiftDragRef.current = nextState;
      setWorkflowShiftDrag(nextState);
      setHoveredTimelineMarkers(buildWorkflowShiftHoverState(nextState));
    };

    const finishDrag = (pointerEvent, canceled = false) => {
      window.removeEventListener('pointermove', updateDrag);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
      window.removeEventListener('keydown', handleKeyDown, true);
      document.body.classList.remove('is-timeline-workflow-shift-dragging');

      if (!canceled) {
        updateDrag(pointerEvent);
      }
      const finalState = workflowShiftDragRef.current;

      if (canceled || !finalState?.isActive || finalState.deltaMinutes === 0) {
        workflowShiftDragRef.current = null;
        setWorkflowShiftDrag(null);
        setHoveredTimelineMarkers(null);
        return;
      }

      if (guardBillingMutation()) {
        workflowShiftDragRef.current = null;
        setWorkflowShiftDrag(null);
        setHoveredTimelineMarkers(null);
        return;
      }

      const previewState = {
        ...finalState,
        isActive: true,
      };

      workflowShiftDragRef.current = previewState;
      setWorkflowShiftDrag(previewState);
      setHoveredTimelineMarkers(buildWorkflowShiftHoverState(previewState));

      if (finalState.mode === 'selection') {
        setShiftRequest({
          mode: 'selection',
          date: finalState.date,
          initialDeltaMinutes: finalState.deltaMinutes,
          context: {
            items: finalState.items ?? [],
            selectionCount: (finalState.items ?? []).length,
            containsRegular: (finalState.items ?? []).some((item) => item.workflowType === 'regular'),
          },
        });
        return;
      }

      setShiftRequest({
        mode: 'workflow',
        date: finalState.date,
        initialDeltaMinutes: finalState.deltaMinutes,
        context: {
          workflowEntity: finalState.workflowEntity,
          workflowRowId: finalState.workflowRowId,
          workflowType: finalState.workflowType,
          workflowName: finalState.workflowName,
          workflowId: finalState.workflowId,
          timeMode: finalState.timeMode,
          anchorTime: finalState.anchorTime,
          occurrenceStartMinutes: finalState.occurrenceStartMinutes,
          occurrenceEndMinutes: finalState.occurrenceEndMinutes,
        },
      });
    };

    function handlePointerUp(pointerEvent) {
      finishDrag(pointerEvent, false);
    }

    function handlePointerCancel(pointerEvent) {
      finishDrag(pointerEvent, true);
    }

    function handleKeyDown(keyboardEvent) {
      if (keyboardEvent.key !== 'Escape') {
        return;
      }

      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      finishDrag(keyboardEvent, true);
    }

    window.addEventListener('pointermove', updateDrag);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
    window.addEventListener('keydown', handleKeyDown, true);
  }, [
    selectedDate,
    selectedWorkflowKeySet,
    selectedWorkflowKeys.length,
    selectedWorkflowShiftItems,
    shiftBusy,
    totalMinutes,
    zoom,
  ]);

  const updateWorkflowShiftPreviewDelta = useCallback((deltaMinutes) => {
    if (!Number.isInteger(deltaMinutes)) {
      return;
    }

    const current = workflowShiftDragRef.current;
    if (!current?.isActive) {
      return;
    }

    const nextState = {
      ...current,
      deltaMinutes,
    };

    workflowShiftDragRef.current = nextState;
    setWorkflowShiftDrag(nextState);
    setHoveredTimelineMarkers(buildWorkflowShiftHoverState(nextState));
  }, []);

  const closeShiftModal = useCallback(() => {
    workflowShiftDragRef.current = null;
    setWorkflowShiftDrag(null);
    setHoveredTimelineMarkers(null);
    setShiftRequest(null);
  }, []);

  const submitShift = useCallback(async ({
    action = 'shift',
    deltaMinutes,
    regularScope,
    copyTarget = 'day-workflow',
  }) => {
    const currentRequest = shiftRequest;
    if (!currentRequest) {
      return;
    }

    if (action === 'copy') {
      const entityAction = buildShiftedWorkflowCopyEntityAction({
        rows: workflows,
        request: currentRequest,
        deltaMinutes,
        copyTarget,
      });

      if (!entityAction) {
        dispatchToast({
          variant: 'warning',
          title: 'Timeline',
          message: 'Die Kopie kann nicht vorbereitet werden. Bitte wähle den Workflow erneut aus.',
          delay: 7000,
        });
        return false;
      }

      dispatchTimelineEntityAction(entityAction);
      return true;
    }

    setShiftBusy(true);
    try {
      if (currentRequest.mode === 'day') {
        const result = await shiftTimelineDay({
          date: currentRequest.date,
          delta_minutes: deltaMinutes,
        });

        dispatchToast({
          variant: 'success',
          title: 'Timeline',
          message: `${Number(result?.count ?? 0)} Workflow(s) verschoben.`,
        });
      } else if (currentRequest.mode === 'selection') {
        const items = currentRequest.context?.items ?? [];
        const bounds = getWorkflowShiftBounds(items, totalMinutes);
        if (
          deltaMinutes < bounds.minDeltaMinutes
          || deltaMinutes > bounds.maxDeltaMinutes
        ) {
          dispatchToast({
            variant: 'warning',
            title: 'Timeline',
            message: 'Die Auswahl kann nicht verschoben werden, weil mindestens ein Workflow den aktiven Tag verlassen würde.',
            delay: 7000,
          });
          return false;
        }

        const payloadItems = buildWorkflowShiftPayloadItems(items);
        if (payloadItems.length < 1) {
          dispatchToast({
            variant: 'warning',
            title: 'Timeline',
            message: 'Die Workflow-Auswahl ist nicht mehr gültig. Bitte wähle die Workflows erneut aus.',
            delay: 7000,
          });
          return false;
        }

        const result = await shiftTimelineWorkflows({
          date: currentRequest.date,
          items: payloadItems,
          delta_minutes: deltaMinutes,
          regular_scope: regularScope,
        });

        dispatchToast({
          variant: 'success',
          title: 'Timeline',
          message: `${Number(result?.count ?? payloadItems.length)} Workflow(s) verschoben.`,
        });
        setSelectedWorkflowKeys([]);
      } else {
        const shifted = await applyWorkflowShift({
          date: currentRequest.date,
          workflowEntity: currentRequest.context.workflowEntity,
          workflowRowId: currentRequest.context.workflowRowId,
          deltaMinutes,
          regularScope,
        });

        return shifted;
      }

      await reload(currentRequest.date);
      return true;
    } catch (error) {
      const operation = action === 'copy' ? 'Kopieren' : 'Verschieben';
      dispatchToast({
        variant: 'danger',
        title: 'Timeline',
        message: `${operation} fehlgeschlagen: ${error?.message ?? String(error)}`,
        delay: 7000,
      });
      return false;
    } finally {
      setShiftBusy(false);
    }
  }, [applyWorkflowShift, reload, shiftRequest, totalMinutes, workflows]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }

    const currentRows = Array.isArray(workflows) ? workflows : [];
    const oldIndex = currentRows.findIndex((row) => String(row?.id) === String(active.id));
    const newIndex = currentRows.findIndex((row) => String(row?.id) === String(over.id));

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const reorderedRows = arrayMove(currentRows, oldIndex, newIndex);
    setWorkflows(reorderedRows);

    void persistRowOrder(buildTimelineRowOrderPayload(reorderedRows))
      .catch((error) => {
        setWorkflows(currentRows);
        dispatchToast({
          variant: 'danger',
          title: 'Timeline',
          message: `Reihenfolge konnte nicht gespeichert werden: ${error?.message ?? String(error)}`,
          delay: 7000,
        });
        void reload(selectedDate);
      });
  }, [persistRowOrder, reload, selectedDate, setWorkflows, workflows]);

  const activeTimelineHoverState = useMemo(
    () => buildWorkflowShiftHoverState(workflowShiftDrag) ?? hoveredTimelineMarkers,
    [hoveredTimelineMarkers, workflowShiftDrag],
  );
  const marqueeSelectionStyle = useMemo(
    () => buildMarqueeSelectionStyle(marqueeSelection),
    [marqueeSelection],
  );

  return (
    <div className="main-container" data-testid="timeline-root">
      <TimelineSidebar
        collapsed={sidebar.collapsed}
        onToggleCollapsed={() => sidebar.setCollapsed((current) => !current)}
        workflowOptions={sidebar.workflowOptions}
        workflowsLoading={sidebar.workflowsLoading}
        selectedWorkflowId={sidebar.selectedWorkflowId}
        onSelectedWorkflowIdChange={sidebar.setSelectedWorkflowId}
        workflowTasks={sidebar.workflowTasks}
        workflowDetailLoading={sidebar.workflowDetailLoading}
        startDate={sidebar.startDate}
        onStartDateChange={sidebar.setStartDate}
        timeMode={sidebar.timeMode}
        onTimeModeChange={sidebar.setTimeMode}
        timeValue={sidebar.timeValue}
        onTimeValueChange={sidebar.setTimeValue}
        dayWorkflowPlanning={sidebar.dayWorkflowPlanning}
        onApply={sidebar.applySelectedWorkflow}
        applying={sidebar.applying}
      />

      <div id="timelineContainer">
        <div className="timeline-app">
          {selectedWorkflowCount > 0 ? (
            <div
              className="timeline-selection-toolbar"
              role="region"
              aria-label="Timeline-Auswahl"
              data-testid="timeline-selection-toolbar"
            >
              <span className="timeline-selection-toolbar-count">
                {selectedWorkflowCount} Workflow{selectedWorkflowCount === 1 ? '' : 's'} ausgewählt
              </span>
              <button
                type="button"
                className="timeline-selection-toolbar-action"
                onClick={openSelectedWorkflowShift}
                disabled={shiftBusy}
                data-testid="timeline-selection-shift"
              >
                Verschieben
              </button>
              <button
                type="button"
                className="timeline-selection-toolbar-action is-muted"
                onClick={clearWorkflowSelection}
                disabled={shiftBusy}
                data-testid="timeline-selection-clear"
              >
                Auswahl aufheben
              </button>
            </div>
          ) : null}

          {!sidebar.collapsed ? (
            <button
              id="timelineMobileRunningTaskStatus"
              type="button"
              className="timeline-mobile-running-task-status d-none"
              title="Zur aktuellen Uhrzeit springen"
            >
              <span className="timeline-mobile-running-task-main">
                <span id="timelineMobileRunningTaskTitle" className="timeline-mobile-running-task-title">-</span>
                <span id="timelineMobileRunningTaskMeta" className="timeline-mobile-running-task-meta">-</span>
              </span>
              <span id="timelineMobileRunningTaskCountdown" className="timeline-mobile-running-task-countdown">-</span>
            </button>
          ) : null}

          <div
            ref={scrollRef}
            className="timeline-scroll"
            data-testid="timeline-scroll"
            onContextMenuCapture={(event) => {
              if (event.target?.closest?.('.timeline-context-menu')) {
                return;
              }

              if (event.target?.closest?.('.workflow-row-drag-handle')) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }

              event.preventDefault();
              event.stopPropagation();
              openTimelineContextMenu(event, resolveTimelineContext(event));
            }}
          >
              <div
                id="timelineReactContent"
                ref={timelineContentRef}
                className={[
                  'timeline-content',
                  dateChangeHighlightActive ? 'is-date-change-highlighted' : '',
                ].filter(Boolean).join(' ')}
                data-testid="timeline-content"
                onPointerDownCapture={startMarqueeSelection}
                style={{
                  width: `${timelineWidthPx}px`,
                  minWidth: '100%',
                  minHeight: '100%',
                  position: 'relative',
                  '--timeline-hour-width': `${60 * BASE_SCALE * zoom}px`,
                  '--timeline-minor-width': `${15 * BASE_SCALE * zoom}px`,
                }}
              >
              {isToday ? (
                <div
                  id="current-time-line"
                  className="current-time-line"
                  style={{ top: 0, bottom: 0, position: 'absolute', zIndex: 50 }}
                />
              ) : null}

              <TimelineHeader
                hours={hours}
                zoom={zoom}
                widthPx={timelineWidthPx}
                startHour={START_HOUR}
                endHour={END_HOUR}
                baseScale={BASE_SCALE}
                timeToPixels={timeToPixels}
                hoverState={activeTimelineHoverState}
                scrollRef={scrollRef}
                contentRef={timelineContentRef}
              />

                <TimelineHoverMarkerLines
                  hoverState={activeTimelineHoverState}
                  zoom={zoom}
                  timeToPixels={timeToPixels}
                />

              {marqueeSelectionStyle ? (
                <div
                  className="timeline-marquee-selection"
                  style={marqueeSelectionStyle}
                  aria-hidden="true"
                />
              ) : null}

              {isTimelineEmpty ? (
                <TimelineEmptyOnboarding
                  selectedDate={selectedDate}
                  onImported={handleStarterTemplateImported}
                  onImportingChange={setStarterTemplateImporting}
                />
              ) : null}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                autoScroll={false}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={(workflows ?? []).map((wf) => String(wf.id))}
                  strategy={verticalListSortingStrategy}
                >
                  {(workflows ?? []).map((wf) => (
                    <WorkflowRow
                      key={wf.id}
                      wf={wf}
                      zoom={zoom}
                      timelineWidthPx={timelineWidthPx}
                      baseScale={BASE_SCALE}
                      timeStringToMinutes={timeStringToMinutes}
                      timeToPixels={timeToPixels}
                      onTimelineHoverChange={setHoveredTimelineMarkers}
                      onTimelineContextMenu={openTimelineContextMenu}
                      onWorkflowShiftDragStart={startWorkflowShiftDrag}
                      onWorkflowSelectionToggle={toggleWorkflowSelection}
                      workflowShiftDrag={workflowShiftDrag}
                      activeHoverKind={activeTimelineHoverState?.kind ?? null}
                      activeHoverWorkflowId={activeTimelineHoverState?.workflowId ?? null}
                      activeHoverOccurrenceId={activeTimelineHoverState?.occurrenceId ?? null}
                      activeHoverForceWorkflowLabel={Boolean(activeTimelineHoverState?.forceWorkflowHoverLabel)}
                      isNewlyInserted={recentlyInsertedRowKeys.includes(buildTimelineRowHighlightKeyFromWorkflow(wf))}
                      isSelected={selectedWorkflowKeySet.has(buildTimelineRowHighlightKeyFromWorkflow(wf))}
                      isSelectionPreview={marqueePreviewWorkflowKeySet.has(buildTimelineRowHighlightKeyFromWorkflow(wf))}
                      selectionModeActive={selectedWorkflowCount > 0}
                      isToday={isToday}
                      currentMinute={currentMinute}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>
      </div>

      <TimelineContextMenu
        open={Boolean(timelineContextMenu?.open)}
        x={timelineContextMenu?.x ?? 0}
        y={timelineContextMenu?.y ?? 0}
        context={timelineContextMenu?.context ?? null}
        onClose={() => setTimelineContextMenu(null)}
        onCreateDayWorkflow={createDayWorkflowFromContext}
        onCreateRegularWorkflow={createRegularWorkflowFromContext}
        onEditTask={editTaskFromContext}
        onShiftWorkflow={shiftWorkflowFromContext}
        onShiftDay={shiftDayFromContext}
        onEditWorkflow={editWorkflowFromContext}
        onDeleteWorkflow={deleteWorkflowFromContext}
      />

      <TimelineShiftModal
        request={shiftRequest}
        busy={shiftBusy}
        onClose={closeShiftModal}
        onPreviewDeltaChange={updateWorkflowShiftPreviewDelta}
        onSubmit={submitShift}
      />

      <TimelineTaskInfoModal
        task={taskInfoModal}
        onClose={closeTaskInfoModal}
      />
    </div>
  );
}
